import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderBar from "../HeaderBar/HeaderBar";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import * as XLSX from "xlsx";
import styles from "./PounceBulkUploadPage.module.css";

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_LEAD_ROWS = 500;
const ALLOWED_EXTENSIONS = ["csv", "xlsx"];

const HEADER_ALIASES = {
  name: ["name", "fullname"],
  phone: [
    "phone",
    "phonenumber",
    "mobile",
    "mobilenumber",
    "phonenumberwithoutcountrycode",
    "phonewithoutcountrycode",
    "phonenumbermustbewithoutcountrycode",
  ],
  email: ["email", "emailaddress"],
  source: ["source", "leadsource"],
  company: ["company", "companyname", "organisation", "organization"],
};

const normalizeHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const isBlankRow = (row) =>
  !Array.isArray(row) || row.every((cell) => String(cell ?? "").trim() === "");

const findUniqueColumn = (headers, aliases, label) => {
  const matches = headers
    .map((header, index) => (aliases.includes(header) ? index : -1))
    .filter((index) => index >= 0);

  if (matches.length > 1) {
    throw new Error(`More than one ${label} column was found. Keep only one.`);
  }
  return matches[0] ?? -1;
};

// "9.17814E+11" — a number Excel shortened for display.
const SCIENTIFIC_NUMBER = /^\d+(\.\d+)?e\+?\d+$/i;

const phoneText = (shown, stored) => {
  if (typeof stored === "number" && Number.isFinite(stored)) {
    return Math.round(stored).toLocaleString("en-US", { useGrouping: false });
  }
  return String(shown ?? "").trim();
};

const parseLeadFile = async (file) => {
  const workbook = XLSX.read(await file.arrayBuffer(), {
    type: "array",
    raw: false,
  });
  const firstSheetName = workbook.SheetNames?.[0];
  if (!firstSheetName)
    throw new Error("This file does not contain a worksheet.");

  const worksheet = workbook.Sheets[firstSheetName];
  const matrix = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    blankrows: true,
    raw: false,
  });
  // The same rows as stored values. Excel shows a long number as
  // "9.17814E+11", and that displayed text is what `matrix` holds — read as
  // the phone, the lead got a number that cannot be called. The stored value
  // is still the whole number.
  const rawMatrix = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    blankrows: true,
    raw: true,
  });
  const headerIndex = matrix.findIndex((row) => !isBlankRow(row));
  if (headerIndex < 0) throw new Error("No header row was found in this file.");

  const headerRow = matrix[headerIndex];
  const headers = headerRow.map(normalizeHeader);
  const nameIndex = findUniqueColumn(headers, HEADER_ALIASES.name, "Name");
  const phoneIndex = findUniqueColumn(headers, HEADER_ALIASES.phone, "Phone");
  const missing = [
    nameIndex < 0 ? "Name" : null,
    phoneIndex < 0 ? "Phone" : null,
  ].filter(Boolean);
  if (missing.length) {
    throw new Error(
      `Missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(" and ")}.`,
    );
  }

  const emailIndex = findUniqueColumn(headers, HEADER_ALIASES.email, "Email");
  const sourceIndex = findUniqueColumn(
    headers,
    HEADER_ALIASES.source,
    "Lead Source",
  );
  const companyIndex = findUniqueColumn(
    headers,
    HEADER_ALIASES.company,
    "Company",
  );

  // Reject any columns that are not in the supported set
  const supportedAliases = new Set(Object.values(HEADER_ALIASES).flat());
  const unsupportedHeaders = headers.filter(
    (h) => h !== "" && !supportedAliases.has(h),
  );
  if (unsupportedHeaders.length) {
    throw new Error(
      `Unsupported column${unsupportedHeaders.length > 1 ? "s" : ""}: ${unsupportedHeaders.join(", ")}. Supported columns are: Name, Phone, Email, Source, Company.`,
    );
  }

  const dataRows = matrix
    .slice(headerIndex + 1)
    .map((cells, index) => ({
      cells,
      rowNumber: headerIndex + index + 2,
      phone: phoneText(
        cells[phoneIndex],
        rawMatrix[headerIndex + 1 + index]?.[phoneIndex],
      ),
    }))
    .filter(({ cells }) => !isBlankRow(cells));

  if (!dataRows.length)
    throw new Error("No lead rows were found below the header.");

  // A CSV saved by Excel holds only the shortened "9.17814E+11" — the other
  // digits are gone from the file, so there is nothing to recover.
  const shortenedRows = dataRows
    .filter(({ phone }) => SCIENTIFIC_NUMBER.test(phone))
    .map(({ rowNumber }) => rowNumber);
  if (shortenedRows.length) {
    throw new Error(
      `The phone number in row${shortenedRows.length > 1 ? "s" : ""} ${shortenedRows.join(", ")} was shortened by Excel (like 9.17814E+11). ` +
        "Format the Phone column as Text, type the numbers again, and save — or upload the .xlsx file instead of a CSV.",
    );
  }

  const multiLeadRows = dataRows
    .filter(({ phone }) => {
      const phoneCell = phone;
      const phoneValues = phoneCell
        .split(/[;,|\n]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      return (
        phoneValues.length > 1 &&
        phoneValues.every((value) => /\d{5,}/.test(value))
      );
    })
    .map(({ rowNumber }) => rowNumber);

  if (multiLeadRows.length) {
    throw new Error(
      `More than one phone number was found in row${multiLeadRows.length > 1 ? "s" : ""} ${multiLeadRows.join(", ")}. Put one lead per row.`,
    );
  }

  const rows = dataRows.map(({ cells, rowNumber, phone }) => ({
    rowNumber,
    name: String(cells[nameIndex] ?? "").trim(),
    phone,
    email: emailIndex >= 0 ? String(cells[emailIndex] ?? "").trim() : "",
    source: sourceIndex >= 0 ? String(cells[sourceIndex] ?? "").trim() : "",
    company: companyIndex >= 0 ? String(cells[companyIndex] ?? "").trim() : "",
  }));

  return {
    rows,
    headers: headerRow.map((header) => String(header ?? "").trim()),
  };
};

const PounceBulkUploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state ?? {};
  const inputRef = useRef(null);
  const [file, setFile] = useState(() => {
    if (routeState.uploadedFile) return routeState.uploadedFile;
    if (routeState.file) return routeState.file;
    if (routeState.fileName) {
      return { name: routeState.fileName, size: routeState.fileSize || 0 };
    }
    return null;
  });
  const [parsedRows, setParsedRows] = useState(() =>
    Array.isArray(routeState.rows) ? routeState.rows : [],
  );
  const [fileError, setFileError] = useState("");
  const [continueMessage, setContinueMessage] = useState("");
  const [isCheckingFile, setIsCheckingFile] = useState(false);
  const [dragging, setDragging] = useState(false);

  const returnTo =
    typeof location.state?.returnTo === "string" &&
    location.state.returnTo.startsWith("/")
      ? location.state.returnTo
      : "/add-lead";

  const validateFile = (candidate) => {
    if (!candidate) return "Choose a CSV or XLSX file to continue.";

    const extension = candidate.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return "Unsupported file type. Choose a CSV or XLSX file.";
    }
    if (candidate.size === 0) return "This file is empty. Choose another file.";
    if (candidate.size > MAX_FILE_BYTES)
      return "The file is too large. Maximum size is 5 MB.";
    return "";
  };

  const acceptFile = async (candidate) => {
    setFile(null);
    setParsedRows([]);
    setFileError("");
    setContinueMessage("");

    const basicError = validateFile(candidate);
    if (basicError) {
      setFileError(basicError);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setIsCheckingFile(true);
    try {
      const parsed = await parseLeadFile(candidate);
      setFile(candidate);
      setParsedRows(parsed.rows);
    } catch (error) {
      setFileError(
        error?.message ||
          "We couldn't read this file. Check the file and try again.",
      );
    } finally {
      setIsCheckingFile(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onInputChange = (event) => {
    const candidate = event.target.files?.[0];
    if (candidate) void acceptFile(candidate);
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const candidate = event.dataTransfer.files?.[0];
    if (candidate) void acceptFile(candidate);
  };

  const downloadTemplate = () => {
    const rows = [
      [
        "Full Name",
        "Phone Number (without country code)",
        "Email Address",
        "Lead Source",
      ],
      ["Aarav Sharma", "4155551234", "aarav@example.com", "Manual Add"],
    ];
    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(","),
      )
      .join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "pounce-leads-template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const continueToReview = async () => {
    if (!file || fileError || isCheckingFile || parsedRows.length === 0) return;
    const fileUrl =
      typeof File !== "undefined" && file instanceof Blob
        ? URL.createObjectURL(file)
        : routeState.fileUrl || null;
    navigate("/bulk-upload/review", {
      state: {
        rows: parsedRows,
        fileName: file.name,
        fileSize: file.size,
        fileUrl,
        uploadedFile: file,
        file,
        returnTo,
      },
    });
  };

  const goBack = () => navigate(returnTo);

  return (
    <div className={styles.page}>
      <div className={styles.stickyTop}>
        <HeaderBar
          title=""
          subtitle={<OutboundCallerBrand />}
          onBack={goBack}
        />
      </div>

      <main className={styles.main}>
        <div className={styles.intro}>
          <h1 className={styles.title}>Upload Leads</h1>
          <p className={styles.subtitle}>
            Add multiple leads by uploading a CSV or XLSX file. We'll validate
            your file and show you a summary before importing.
          </p>
        </div>

        <div className={styles.uploadGroup}>
          <div
            className={`${styles.dropZone} ${dragging ? styles.dropZoneActive : ""} ${file ? styles.dropZoneSelected : ""}`}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                setDragging(false);
            }}
            onDrop={onDrop}
          >
            <input
              ref={inputRef}
              className={styles.fileInput}
              type="file"
              disabled={isCheckingFile}
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onInputChange}
              aria-label="Choose a CSV or XLSX file"
            />

            <span className={styles.uploadIcon} aria-hidden="true">
              <svg width="31" height="31" viewBox="0 0 32 32" fill="none">
                <path
                  d="M9 3.5h9l8 8V27a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 8 27V5a1.5 1.5 0 0 1 1-1.5Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 4v8h8M16 23V15m0 0-4 4m4-4 4 4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>

            {isCheckingFile ? (
              <>
                <p className={styles.dropTitle}>Checking your file…</p>
                <p className={styles.dropHint}>
                  Reading the file and checking its columns
                </p>
              </>
            ) : file ? (
              <>
                <p className={styles.dropTitle}>{file.name}</p>
                <p className={styles.dropHint}>
                  {(file.size / 1024).toFixed(0)} KB · Ready to continue
                </p>
                <button
                  className={styles.replaceFile}
                  type="button"
                  onClick={() => inputRef.current?.click()}
                >
                  Change file
                </button>
              </>
            ) : (
              <>
                <p className={styles.dropTitle}>Drag and drop your file here</p>
                <p className={styles.dropHint}>Supports CSV or XLSX files</p>
                <button
                  className={styles.chooseButton}
                  type="button"
                  onClick={() => inputRef.current?.click()}
                >
                  Choose File
                </button>
              </>
            )}
          </div>

          {fileError && (
            <p className={styles.fileError} role="alert">
              {fileError}
            </p>
          )}

          {file && !fileError && !isCheckingFile && (
            <p className={styles.fileSuccess} role="status">
              File checked · {parsedRows.length} lead rows found
            </p>
          )}

          {/* {file && !fileError && (
            <button
              className={styles.removeFile}
              type="button"
              onClick={() => {
                setFile(null);
                setParsedRows([]);
              }}
            ></button>
          )} */}
        </div>

        <button
          className={styles.templateCard}
          type="button"
          onClick={downloadTemplate}
        >
          <span className={styles.templateIcon} aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20V3.5Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M14 3.5V8h4M9 12h6M9 15h6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className={styles.templateCopy}>
            <strong>Download Sample Template</strong>
            <small>Use our template to ensure the best results.</small>
          </span>
          <svg
            className={styles.downloadIcon}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 3v12m0 0 4-4m-4 4-4-4M5 18v2h14v-2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <section
          className={styles.requirements}
          aria-labelledby="file-requirements-title"
        >
          <h2 id="file-requirements-title">
            File requirements
            <span
              className={styles.infoIcon}
              title="Check these before uploading"
              aria-label="File requirements information"
            >
              ⓘ
            </span>
          </h2>
          <ul>
            <li>Supported formats: CSV, XLSX</li>
            <li>Maximum file size: 5 MB</li>
            <li>Include at least Name and Phone number</li>
            <li>Phone number must be without country code </li>
            <li>One lead per row</li>
            <li>Maximum leads per upload: 500</li>
            <li>Ensure you have consent to be contacted</li>
          </ul>
        </section>

        <button
          className={styles.continueButton}
          type="button"
          disabled={
            !file ||
            Boolean(fileError) ||
            isCheckingFile ||
            parsedRows.length === 0
          }
          onClick={continueToReview}
        >
          Continue
        </button>
        {continueMessage && (
          <p className={styles.continueMessage} role="status">
            {continueMessage}
          </p>
        )}
      </main>
    </div>
  );
};

export default PounceBulkUploadPage;
